import { Accordion, Text, Link, MessageInline } from '@afterpaytouch/core'
import React, { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react'
import { ShippingAddressItem } from '..'
import { Trans } from '../../utils/convergence'
import { editShippingAddressRoute, Route, useNavigateToShippingAddress } from '../../config/router'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import {
  AddressModeType,
  ShippingAddressStatus,
  ShippingOptionErrorType,
  ShippingAddress as ShippingAddressModel,
  money,
  ExpandableComponent,
} from '../../model'
import { TrackingEvent } from '../../model/monitoring'
import {
  clearShippingOptionError,
  setShippingOptionError,
  useAppDispatch,
  useAppRouter,
  useCheckoutState,
  useIsDisabledUpdatingCheckout,
  useIsPickup,
  useShippingAddress,
  useShippingOption,
  useIsShippingAddressValid,
  useIsDeferredShipping,
  updateCheckout,
  CheckoutAppDispatch,
  useInitialData,
  setChosenAddress,
  useExpandedComponentControl,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { isUndefinedOrEmpty } from '@afterpay/utils/string'

import { unwrapResult } from '@reduxjs/toolkit'
import { isValidAddress } from '../../utils/profile-validation'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { useTranslation } from '../../utils/convergence'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { ShippingAddressListRedesigned } from '../ShippingAddressListRedesigned'
import { useCheckoutColor } from '../../utils/color'
import { useShippingAddressInlineErrorKey, useShippingAddressInlineLabels } from './hooks'
import styles from './style.module.css'

export const ShippingAddressInline: FunctionComponent = () => {
  const { chosenAddress, pickupAddress, shippingAddressStatus, isShippingAddressChanged } = useShippingAddress()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const router = useAppRouter()
  const { error } = useShippingOption()
  const isDisabledFromUpdatingCheckout = useIsDisabledUpdatingCheckout()
  const { t } = useTranslation()
  const isPickup = useIsPickup()
  const { checkout } = useCheckoutState()
  const { headingTextColor, subtextColor } = useCheckoutColor()
  const { smallTextSize, textSize } = useCheckoutSizes()
  const dispatch = useAppDispatch()
  const hasValidShippingAddress = useIsShippingAddressValid()
  const isDeferredShipping = useIsDeferredShipping()
  const { addressMode } = useInitialData()
  const navigateToShippingAddress = useNavigateToShippingAddress()

  const { setExpandedState, toggleExpandedState, expandableComponentState } = useExpandedComponentControl(ExpandableComponent.SHIPPING_ADDRESS_INLINE)
  const isExpanded = expandableComponentState === 'Expanded'

  const isShippingAddressLoading = !isPickup && shippingAddressStatus <= ShippingAddressStatus.Initialising
  const shippingAddress = isPickup ? pickupAddress : chosenAddress
  const isStaticAddress = isPickup || addressMode === AddressModeType.SHIP_TO_ORDER_ADDRESS
  const { heading, description } = useShippingAddressInlineLabels()
  const shippingAddressInlineErrorKey = useShippingAddressInlineErrorKey()
  const hasError = valueIsNotNullOrUndefined(shippingAddressInlineErrorKey)
  const isAccordionDisabled = isDisabledFromUpdatingCheckout || isShippingAddressLoading || !hasValidShippingAddress

  const defaultMoney = money('0.00', checkout?.amount?.currency)

  const doUpdateCheckout = (): CheckoutAppDispatch => {
    // @ts-expect-error TODO: fix type error
    return dispatch(
      updateCheckout({
        amount: checkout?.amount ?? defaultMoney,
        shippingAddressId: isPickup ? undefined : chosenAddress.id,
      })
    )
  }

  const updateCheckoutIfRequired = (): void => {
    if (isShippingAddressChanged && isDeferredShipping) {
      try {
        const response = doUpdateCheckout()

        // @ts-expect-error TODO: fix type error
        unwrapResult(response)
      } catch (e) {
        capture.error('Error updating checkout on address change', { ...e })

        router.push(Route.TERMINAL_ERROR)
      }
    }
  }

  useEffect(() => {
    if (hasValidShippingAddress) {
      const valid = !isUndefinedOrEmpty(chosenAddress?.phoneNumber) && isValidAddress(chosenAddress)

      if (!valid && !isPickup && addressMode !== AddressModeType.NO_ADDRESS) {
        const incompleteAddressError = ShippingOptionErrorType.ShippingAddressIncomplete
        dispatch(setShippingOptionError(incompleteAddressError))
      }

      if (valid && error === ShippingOptionErrorType.ShippingAddressIncomplete) {
        dispatch(clearShippingOptionError())
      }

      if (valid || isPickup) {
        updateCheckoutIfRequired()
      }
    }
  }, [chosenAddress])

  const currAddressRef = useRef<ShippingAddressModel>(chosenAddress)
  const [disableNewAddressAction, setDisableNewAddressAction] = useState(false)

  const handleNewAddressClick = (): void => {
    setDisableNewAddressAction(true)
    logEvent(TrackingEvent.PRESSED_ADD_NEW_SHIPPING_ADDRESS)
    router.push(Route.NEW_SHIPPING_ADDRESS)
  }

  const handleShippingAddressChange = (address: ShippingAddressModel | undefined): void => {
    setDisableNewAddressAction(true)
    logEvent(TrackingEvent.SELECTED_SHIPPING_ADDRESS, address)
    if (valueIsNotNullOrUndefined(address)) {
      currAddressRef.current = address
    }
    onSave()
  }

  const handleShippingAddressClick = (): void => {
    logEvent(TrackingEvent.PRESSED_CHANGE_SHIPPING_ADDRESS)
    navigateToShippingAddress()
  }

  const editShippingAddress = (): void => {
    logEvent(TrackingEvent.PRESSED_EDIT_SHIPPING_ADDRESS)
    router.push(editShippingAddressRoute(chosenAddress.id))
  }

  const onSave = (): void => {
    const currAddress = currAddressRef.current

    logEvent(TrackingEvent.PRESSED_SHIPPING_ADDRESS_SAVE_BUTTON, { chosenAddress: currAddress })
    dispatch(setChosenAddress(currAddress))
    setDisableNewAddressAction(false)
    setExpandedState('Collapsed')
  }

  const handleShippingAddressAccordionClick = (): void => {
    toggleExpandedState()
    logEvent(TrackingEvent.PRESSED_SUMMARY_SECTION, { section: 'Inline Address', state: isExpanded ? 'Collapsed' : 'Expanded' })
  }

  const renderAccordionLabels = (): ReactNode => {
    return (
      <>
        <Text color={headingTextColor} size={textSize} testNameSpace='shipping-address-inline' bold>
          {t(heading)}
        </Text>
        {valueIsNotNullOrUndefined(description) && (
          <Text size={smallTextSize} color={subtextColor} testNameSpace='shipping-address-inline-description'>
            {t(description)}
          </Text>
        )}
      </>
    )
  }

  const renderHeading = (): ReactNode => {
    return (
      <>
        {renderAccordionLabels()}
        <ShippingAddressItem
          {...shippingAddress}
          disabled={isAccordionDisabled}
          preferred={false}
          isAddressLoading={isShippingAddressLoading}
          isAddressEmpty={!hasValidShippingAddress}
          boldNameLabel={false}
        />
      </>
    )
  }

  const renderError = (): ReactNode => {
    if (!hasError) {
      return null
    }

    return (
      <div className={styles.inlineErrorContainer}>
        <MessageInline
          kind='Error'
          description={
            <Text size={smallTextSize} testNameSpace='shipping-address-inline-error'>
              <Trans
                components={{
                  shippingAddressLink: (
                    <Link onClick={handleShippingAddressClick} kind='Underline' testNameSpace='shipping-address-inline-error-change-address' />
                  ),
                  editAddress: <Link onClick={editShippingAddress} kind='Underline' testNameSpace='shipping-address-inline-error-edit-address' />,
                  addAddressLink: <Link onClick={handleNewAddressClick} kind='Underline' testNameSpace='shipping-address-inline-error-add-address' />,
                }}
                i18nKey={shippingAddressInlineErrorKey}
                values={{ merchantName: checkout?.merchant?.name }}
              />
            </Text>
          }
          testNameSpace='shipping-address-inline-error'
        />
      </div>
    )
  }

  if (addressMode === AddressModeType.NO_ADDRESS) {
    return null
  }

  if (isStaticAddress) {
    const message = isPickup ? t('summary:pickupMessage') : t('summary:shipToOrderAddressMessage')
    return (
      <div className={styles.staticAddressContainer} data-testid='shipping-address'>
        {renderHeading()}
        <Text size={smallTextSize} color={subtextColor} testNameSpace='shipping-address-static-address-message'>
          {message}
        </Text>
      </div>
    )
  }

  return (
    <Accordion
      id={ExpandableComponent.SHIPPING_ADDRESS_INLINE}
      testNameSpace='shipping-address'
      onChange={() => handleShippingAddressAccordionClick()}
      expanded={isExpanded}
      disabled={isAccordionDisabled}
    >
      <Accordion.Button>{renderHeading()}</Accordion.Button>
      {renderError()}
      <Accordion.Details>
        <ShippingAddressListRedesigned
          onChange={(address) => handleShippingAddressChange(address)}
          chosenAddress={hasValidShippingAddress ? chosenAddress : undefined}
          addNewAddressClick={handleNewAddressClick}
          addNewAddressClickDisabled={disableNewAddressAction}
        />
      </Accordion.Details>
    </Accordion>
  )
}
